import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { DocumentNavigationComponent } from 'app/common/components/document-navigation/document-navigation.component';
import { OrderDetailAttributes } from '@albatrosdeveloper/ave-models-package';

/**
 * Serial Navigation Cell Renderer
 *
 * Displays document serial number with navigation capability using DocumentNavigationComponent.
 * Shows clickable link if document type is navigable, otherwise shows disabled state.
 *
 * @example
 * ```typescript
 * {
 *   field: 'serials',
 *   headerName: 'Nro. Doc.',
 *   cellRenderer: SerialNavigationCellRendererComponent,
 *   minWidth: 160
 * }
 * ```
 */
@Component({
  selector: "app-serial-navigation-cell-renderer",
  standalone: true,
  imports: [CommonModule, DocumentNavigationComponent],
  template: `
    <app-document-navigation
      [documentType]="documentType"
      [documentSerial]="documentSerial"
      [displayText]="documentSerial"
      [showIcon]="true"
    >
    </app-document-navigation>
  `,
})
export class SerialNavigationCellRendererComponent
  implements CellRenderer<any>
{
  @Input() params!: CellRendererParams<any>;

  /**
   * Document type for navigation
   * Gets from cellRendererParams if available, falls back to data.documentType
   */
  get documentType(): string {

    return (
      this.params?.cellRendererParams?.documentType ??
      this.params?.data?.type?.code ??
      this.params?.data?.documentBeforeUpdate?.type?.code ??
      this.params?.data?.documentType?.code ??
      this.params?.data?.documentType ?? '');
  }

  /**
   * Document serial number from row data
   * Falls back through multiple paths: serials -> documentBeforeUpdate.serial -> '-'
   */
  get documentSerial(): string {
    return (
      this.params?.data?.serials ??
      this.params?.data?.serial ??
      this.params?.data?.documentBeforeUpdate?.serial ??
      '-'
    );
  }
}
